import { useEffect, useState } from 'react';
import MeditationEvent from '@/models/meditation-event';
import { useEvent, useCreateEvent, useUpdateEvent, useDeleteEvent, EventMutationPayload } from '@/services/events';
import FormModal from '@/shared/components/form-modal';
import { MaterialFormContainer, MaterialFormOption, MaterialTextField } from '@/shared/components/form/MaterialFormElements';
import { IANA_TIME_ZONES, isValidIanaTimeZone } from '@/utils/timezones';

interface EventModalProps {
    isOpen: boolean;
    selectedEventId?: string;
    toggle: () => void;
}

interface PromoCodeForm {
    code: string;
    discountPercent: number | '';
}

interface EventForm {
    slug: string;
    title: string;
    description: string;
    startsAt: string; // datetime-local string, in the event timezone
    endsAt: string; // datetime-local string, in the event timezone
    timezone: string;
    location: string;
    capacity: number | '';
    active: boolean;
    orderIndex: number | '';
    price: number | ''; // Toman; '' clears it (→ free event)
    promoCodes: PromoCodeForm[];
}

// New events default to the standard 3,000,000 Toman fee (same for every event today);
// clear the price to make an event free.
const DEFAULT_EVENT_PRICE = 3_000_000;

const emptyForm = (): EventForm => ({
    slug: '',
    title: '',
    description: '',
    startsAt: '',
    endsAt: '',
    timezone: '',
    location: '',
    capacity: '',
    active: true,
    orderIndex: 0,
    price: DEFAULT_EVENT_PRICE,
    promoCodes: [],
});

const slugify = (value: string): string =>
    value
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

// ── Timezone-aware conversion helpers (lifted from the cohort modal). The admin
//    enters the event's wall-clock IN THE VENUE ZONE; we store the absolute UTC
//    instant, and site/app render it in each visitor's own local time. ──────────

// Offset (ms, zone − UTC) at a given instant — DST-correct, no external dep.
const zoneOffsetMs = (date: Date, timeZone: string): number => {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).formatToParts(date);
    const p: Record<string, number> = {};
    for (const part of parts) if (part.type !== 'literal') p[part.type] = Number(part.value);
    return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - date.getTime();
};

// UTC ISO → the `YYYY-MM-DDTHH:mm` a datetime-local input wants, read in `timeZone`.
// Falls back to browser-local for a legacy/invalid zone (non-breaking).
const isoToLocalInput = (iso: string | undefined, timeZone: string): string => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    if (!isValidIanaTimeZone(timeZone)) {
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    }).formatToParts(d);
    const g = (t: string) => parts.find((x) => x.type === t)?.value ?? '';
    return `${g('year')}-${g('month')}-${g('day')}T${g('hour')}:${g('minute')}`;
};

// datetime-local value (wall-clock in `timeZone`) → UTC ISO. Two-pass for DST edges.
const localInputToIso = (value: string, timeZone: string): string => {
    if (!value) return '';
    if (!isValidIanaTimeZone(timeZone)) {
        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? '' : d.toISOString();
    }
    const approx = new Date(`${value}:00.000Z`);
    if (Number.isNaN(approx.getTime())) return '';
    let instant = new Date(approx.getTime() - zoneOffsetMs(approx, timeZone));
    instant = new Date(approx.getTime() - zoneOffsetMs(instant, timeZone));
    return instant.toISOString();
};

const Hint = ({ children }: { children: React.ReactNode }) => (
    <p style={{ margin: '-6px 0 10px', fontSize: 12, color: '#999999', lineHeight: 1.5 }}>{children}</p>
);

const EventModal = ({ isOpen, toggle, selectedEventId }: EventModalProps) => {
    const { data: event, isLoading } = useEvent(selectedEventId as string, isOpen);
    const { mutateAsync: createEvent, isPending: isCreating } = useCreateEvent();
    const { mutateAsync: updateEvent, isPending: isUpdating } = useUpdateEvent();
    const { mutateAsync: deleteEvent, isPending: isDeleting } = useDeleteEvent();

    const [form, setForm] = useState<EventForm>(emptyForm());
    const [registeredCount, setRegisteredCount] = useState(0);
    const [error, setError] = useState<string | null>(null);
    // Id of an event just created in this session — a re-click Saves as an UPDATE.
    const [createdEventId, setCreatedEventId] = useState<string | null>(null);

    // `isOpen` in the deps so re-opening the SAME event re-fills from cached data.
    useEffect(() => {
        if (!isOpen) return;
        if (event?.data) {
            const e: MeditationEvent = event.data;
            setForm({
                slug: e.slug,
                title: e.title,
                description: e.description ?? '',
                startsAt: isoToLocalInput(e.startsAt, e.timezone),
                endsAt: isoToLocalInput(e.endsAt, e.timezone),
                timezone: e.timezone,
                location: e.location ?? '',
                capacity: typeof e.capacity === 'number' ? e.capacity : '',
                active: e.active,
                orderIndex: typeof e.orderIndex === 'number' ? e.orderIndex : 0,
                price: typeof e.price === 'number' ? e.price : '',
                promoCodes: (e.promoCodes ?? []).map((c) => ({ code: c.code, discountPercent: c.discountPercent })),
            });
            setRegisteredCount(e.registeredCount ?? 0);
        } else if (!selectedEventId) {
            setForm(emptyForm());
            setRegisteredCount(0);
        }
        setError(null);
    }, [isOpen, event?.data, selectedEventId]);

    useEffect(() => {
        if (!isOpen) {
            setForm(emptyForm());
            setRegisteredCount(0);
            setError(null);
            setCreatedEventId(null);
        }
    }, [isOpen]);

    const set = <K extends keyof EventForm>(key: K, value: EventForm[K]) => setForm((f) => ({ ...f, [key]: value }));

    const setPromo = (i: number, key: keyof PromoCodeForm, value: string) =>
        setForm((f) => ({
            ...f,
            promoCodes: f.promoCodes.map((c, idx) =>
                idx === i ? { ...c, [key]: key === 'discountPercent' ? (value === '' ? '' : Number(value)) : value } : c,
            ),
        }));
    const addPromo = () => setForm((f) => ({ ...f, promoCodes: [...f.promoCodes, { code: '', discountPercent: '' }] }));
    const removePromo = (i: number) => setForm((f) => ({ ...f, promoCodes: f.promoCodes.filter((_, idx) => idx !== i) }));

    const validate = (): string | null => {
        if (!form.title.trim()) return 'Title is required';
        if (!form.description.trim()) return 'Description is required';
        if (!form.location.trim()) return 'Location is required';
        if (!form.timezone.trim()) return 'Timezone is required';
        if (!isValidIanaTimeZone(form.timezone)) return 'Please select a valid IANA time zone';
        if (!form.startsAt) return 'Start date & time is required';
        if (form.endsAt && form.endsAt <= form.startsAt) return 'End must be after the start';
        if (form.capacity === '' || Number(form.capacity) < 1) return 'Capacity must be at least 1';
        // Capacity below the current headcount would permanently block registration.
        if (Number(form.capacity) < registeredCount) return `Capacity can’t be below the number already registered (${registeredCount}).`;
        if (form.price !== '' && Number(form.price) < 0) return 'Price can’t be negative';
        for (const c of form.promoCodes) {
            const pct = c.discountPercent;
            if (c.code.trim() && (pct === '' || Number(pct) < 1 || Number(pct) > 100))
                return `Promo code “${c.code.trim()}” needs a discount between 1 and 100`;
        }
        return null;
    };

    const buildPayload = (): EventMutationPayload => ({
        slug: form.slug.trim() || slugify(form.title),
        title: form.title.trim(),
        description: form.description.trim(),
        startsAt: localInputToIso(form.startsAt, form.timezone),
        // null (not undefined) so clearing a previously-set end time actually persists.
        endsAt: form.endsAt ? localInputToIso(form.endsAt, form.timezone) : null,
        timezone: form.timezone.trim(),
        location: form.location.trim(),
        capacity: Number(form.capacity),
        active: form.active,
        orderIndex: form.orderIndex === '' ? 0 : Number(form.orderIndex),
        // '' → null clears the fee (free event). Drop incomplete promo rows.
        price: form.price === '' ? null : Number(form.price),
        promoCodes: form.promoCodes
            .filter((c) => c.code.trim() && c.discountPercent !== '')
            .map((c) => ({ code: c.code.trim(), discountPercent: Number(c.discountPercent) })),
    });

    const handleSave = async () => {
        const validationError = validate();
        if (validationError) {
            setError(validationError);
            return;
        }
        setError(null);
        try {
            const payload = buildPayload();
            const existingId = selectedEventId ?? createdEventId;
            // callApi never throws on API errors — inspect result.status.
            const result = existingId ? await updateEvent({ eventId: existingId, ...payload }) : await createEvent(payload);
            if (result.status === 'error') {
                setError(result.error?.message ?? 'Failed to save the event.');
                return;
            }
            if (!existingId && result.data?.id) setCreatedEventId(result.data.id);
            toggle();
        } catch (e) {
            console.error('Failed to save event:', e);
            setError('Failed to save the event. Check the console for details.');
        }
    };

    const handleDelete = async () => {
        const deleteId = selectedEventId ?? createdEventId;
        if (!deleteId) return;
        try {
            const result = await deleteEvent({ eventId: deleteId });
            if (result.status === 'error') {
                setError(result.error?.message ?? 'Failed to delete the event.');
                return;
            }
            toggle();
        } catch (e) {
            console.error('Failed to delete event:', e);
            setError('Failed to delete the event. Check the console for details.');
        }
    };

    const timezoneInvalid = !!form.timezone && !IANA_TIME_ZONES.includes(form.timezone);

    return (
        <FormModal
            isOpen={isOpen}
            showDelete={!!(selectedEventId ?? createdEventId)}
            title={`${(selectedEventId ?? createdEventId) ? 'Edit' : 'New'} Event`}
            body={
                <MaterialFormContainer onSubmit={handleSave}>
                    {error && (
                        <div
                            style={{
                                background: 'rgba(255,107,107,0.12)',
                                color: '#ff8a8a',
                                border: '1px solid rgba(255,107,107,0.35)',
                                padding: 10,
                                borderRadius: 4,
                                marginBottom: 12,
                            }}
                        >
                            {error}
                        </div>
                    )}

                    <MaterialTextField label="Title" value={form.title} onChange={(e) => set('title', e.target.value)} />
                    <MaterialTextField label="Description" multiline value={form.description} onChange={(e) => set('description', e.target.value)} />
                    <MaterialTextField label="Slug (optional)" value={form.slug} onChange={(e) => set('slug', e.target.value)} />
                    <Hint>The stable public id (kebab-case). Leave blank to auto-generate it from the title.</Hint>

                    <MaterialTextField label="Location (venue + city)" value={form.location} onChange={(e) => set('location', e.target.value)} />

                    {/* ── Schedule ─────────────────────────────────────────── */}
                    <p style={{ margin: '18px 0 4px', fontWeight: 600 }}>Schedule</p>
                    <MaterialTextField
                        name="select"
                        select
                        label="Timezone"
                        value={form.timezone}
                        error={timezoneInvalid}
                        onChange={(e) => set('timezone', e.target.value)}
                    >
                        {timezoneInvalid && (
                            <MaterialFormOption value={form.timezone}>{form.timezone} — invalid, please re-select</MaterialFormOption>
                        )}
                        {IANA_TIME_ZONES.map((tz) => (
                            <MaterialFormOption key={tz} value={tz}>
                                {tz}
                            </MaterialFormOption>
                        ))}
                    </MaterialTextField>
                    <Hint>
                        {timezoneInvalid
                            ? 'This is not a valid IANA time zone — please re-select. You enter the event time in this zone; visitors see it in their own local time.'
                            : 'The venue’s IANA time zone. You enter the wall-clock time in this zone; visitors see it converted to their own local time.'}
                    </Hint>
                    <div style={{ display: 'flex', gap: 16 }}>
                        <MaterialTextField
                            label="Starts at"
                            type="datetime-local"
                            InputLabelProps={{ shrink: true }}
                            value={form.startsAt}
                            onChange={(e) => set('startsAt', e.target.value)}
                        />
                        <MaterialTextField
                            label="Ends at (optional)"
                            type="datetime-local"
                            InputLabelProps={{ shrink: true }}
                            value={form.endsAt}
                            onChange={(e) => set('endsAt', e.target.value)}
                        />
                    </div>

                    {/* ── Capacity & visibility ────────────────────────────── */}
                    <p style={{ margin: '18px 0 4px', fontWeight: 600 }}>Capacity & visibility</p>
                    <div style={{ display: 'flex', gap: 16 }}>
                        <MaterialTextField
                            label="Capacity"
                            type="number"
                            inputProps={{ min: 1, step: 1 }}
                            value={form.capacity}
                            onChange={(e) => set('capacity', e.target.value === '' ? '' : Number(e.target.value))}
                        />
                        <MaterialTextField label="Registered" type="number" value={registeredCount} disabled />
                        <MaterialTextField
                            name="select"
                            select
                            label="Show on site"
                            value={form.active ? 'yes' : 'no'}
                            onChange={(e) => set('active', e.target.value === 'yes')}
                        >
                            <MaterialFormOption value="yes">Yes</MaterialFormOption>
                            <MaterialFormOption value="no">No (hidden)</MaterialFormOption>
                        </MaterialTextField>
                    </div>
                    <Hint>Registered is maintained automatically and can’t be edited. Hidden events don’t appear on the site.</Hint>
                    <MaterialTextField
                        label="Display order (optional)"
                        type="number"
                        inputProps={{ step: 1 }}
                        value={form.orderIndex}
                        onChange={(e) => set('orderIndex', e.target.value === '' ? '' : Number(e.target.value))}
                    />
                    <Hint>Lower shows first when two events start at the same time. The site otherwise orders events by start time.</Hint>

                    {/* ── Payment ──────────────────────────────────────────── */}
                    <p style={{ margin: '18px 0 4px', fontWeight: 600 }}>Payment</p>
                    <MaterialTextField
                        label="Price (Toman)"
                        type="number"
                        inputProps={{ min: 0, step: 1 }}
                        value={form.price}
                        onChange={(e) => set('price', e.target.value === '' ? '' : Number(e.target.value))}
                    />
                    <Hint>
                        The fee shown on the registration form. Registrants pay by bank transfer and upload a receipt. Leave blank for a free event.
                        The bank account shown to them is set once under Settings.
                    </Hint>

                    <p style={{ margin: '10px 0 4px', fontSize: 13, fontWeight: 600, color: '#c9c9c9' }}>Promo codes</p>
                    <Hint>Display-only. When a registrant enters a matching code, the form shows the discounted price to pay. 100% = free (no receipt).</Hint>
                    {form.promoCodes.map((c, i) => (
                        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                            <div style={{ flex: 2 }}>
                                <MaterialTextField label="Code" value={c.code} onChange={(e) => setPromo(i, 'code', e.target.value)} />
                            </div>
                            <div style={{ flex: 1 }}>
                                <MaterialTextField
                                    label="Discount %"
                                    type="number"
                                    inputProps={{ min: 1, max: 100, step: 1 }}
                                    value={c.discountPercent}
                                    onChange={(e) => setPromo(i, 'discountPercent', e.target.value)}
                                />
                            </div>
                            <button
                                type="button"
                                onClick={() => removePromo(i)}
                                title="Remove code"
                                style={{ marginTop: 8, padding: '8px 12px', background: 'transparent', color: '#ff8a8a', border: '1px solid rgba(255,107,107,0.35)', borderRadius: 4, cursor: 'pointer' }}
                            >
                                ✕
                            </button>
                        </div>
                    ))}
                    <button
                        type="button"
                        onClick={addPromo}
                        style={{ marginTop: 4, padding: '8px 14px', background: 'transparent', color: '#00c6a9', border: '1px solid rgba(0,198,169,0.4)', borderRadius: 4, cursor: 'pointer' }}
                    >
                        + Add promo code
                    </button>
                </MaterialFormContainer>
            }
            toggle={toggle}
            handleSave={handleSave}
            handleDelete={handleDelete}
            loading={isLoading || isCreating || isUpdating || isDeleting}
        />
    );
};

export default EventModal;
